import api from './api';
import { Member, OrgChartNode, ReportingChainUser, UpcomingBirthday, UpcomingAnniversary, SpringPageResponse, SecurityRole } from '../types';

export interface CreateMemberDto {
  email: string;
  name: string;
  organizationRole?: string;
  reportsToId?: string | null;
  clients?: string[];
  birthDate?: string;
  hireDate?: string;
  contractRate?: number;
}

/** @deprecated Use CreateMemberDto */
export type CreateUserDto = CreateMemberDto;

export interface UpdateMemberDto {
  name?: string;
  email?: string;
  organizationRole?: string;
  reportsToId?: string | null;
  clients?: string[] | null;
  birthDate?: string | null;
  hireDate?: string | null;
  isActive?: boolean;
  offboardingDate?: string | null;
  offboardingReason?: string | null;
  contractRate?: number | null;
}

/** @deprecated Use UpdateMemberDto */
export type UpdateUserDto = UpdateMemberDto;

export interface UpdateProfileDto {
  name?: string;
  birthDate?: string;
  hireDate?: string;
  onboardingCompleted?: boolean;
  // Onboarding fields
  professionalDescription?: string;
  professionalHabit?: string;
  friendGroupKnownFor?: string;
  hobbies?: string;
  lifeGoal?: string;
  funFact?: string;
  careerLearning?: string;
}

/**
 * Member Service
 * Centralizes all member-related API calls
 */
class MemberService {
  private baseUrl = '/members';

  /**
   * Get current member profile
   */
  async getMe(): Promise<Member> {
    const response = await api.get<Member>(`${this.baseUrl}/me`);
    return response.data;
  }

  /**
   * Get all members (paginated)
   */
  async getAll(includeInactive: boolean = false): Promise<Member[]> {
    const params: Record<string, string> = { size: '1000' };
    if (includeInactive) {
      params.includeInactive = 'true';
    }
    const response = await api.get<SpringPageResponse<Member>>(this.baseUrl, { params });
    return response.data.content;
  }

  /**
   * Get a single member by ID
   */
  async getById(id: string): Promise<Member> {
    const response = await api.get<Member>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Create a new member
   */
  async create(data: CreateMemberDto): Promise<Member> {
    const response = await api.post<Member>(this.baseUrl, data);
    return response.data;
  }

  /**
   * Update a member
   */
  async update(id: string, data: UpdateMemberDto): Promise<Member> {
    const response = await api.patch<Member>(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  /**
   * Update the authorization role a member holds in the organization (admin only).
   * This is the role behind permissions, separate from the free-text organizationRole job title.
   */
  async updateRole(id: string, role: SecurityRole): Promise<Member> {
    const response = await api.patch<Member>(`${this.baseUrl}/${id}/role`, { role });
    return response.data;
  }

  /**
   * Update current member's profile
   */
  async updateProfile(data: UpdateProfileDto): Promise<Member> {
    const response = await api.patch<Member>(`${this.baseUrl}/me`, data);
    return response.data;
  }

  /**
   * Delete a member (soft delete)
   */
  async delete(id: string): Promise<void> {
    await api.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Upload own profile photo
   */
  async uploadOwnPhoto(file: File): Promise<Member> {
    const formData = new FormData();
    formData.append('photo', file);

    const response = await api.post<Member>(`${this.baseUrl}/me/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Upload member photo (admin only)
   */
  async uploadPhoto(id: string, file: File): Promise<Member> {
    const formData = new FormData();
    formData.append('photo', file);

    const response = await api.post<Member>(`${this.baseUrl}/${id}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Get all unique client tags
   */
  async getClientTags(): Promise<string[]> {
    const response = await api.get<{ tags: string[] }>(`${this.baseUrl}/clients/tags`);
    return response.data.tags;
  }

  /**
   * Check upcoming birthdays
   */
  async getUpcomingBirthdays(days: number = 7): Promise<UpcomingBirthday[]> {
    const response = await api.get<UpcomingBirthday[]>(`${this.baseUrl}/birthdays/upcoming?days=${days}`);
    return response.data;
  }

  /**
   * Check upcoming work anniversaries
   */
  async getUpcomingAnniversaries(days: number = 30): Promise<UpcomingAnniversary[]> {
    const response = await api.get<UpcomingAnniversary[]>(`${this.baseUrl}/anniversaries/upcoming?days=${days}`);
    return response.data;
  }

  /**
   * Sync all member roles with organizational role admin flags
   */
  async syncRoles(): Promise<void> {
    await api.post(`${this.baseUrl}/sync-roles`);
  }

  /**
   * Get organizational chart hierarchy
   */
  async getOrgChart(): Promise<OrgChartNode[]> {
    const response = await api.get<OrgChartNode[]>(`${this.baseUrl}/org-chart`);
    return response.data;
  }

  /**
   * Get reporting chain for a specific member
   */
  async getReportingChain(userId: string): Promise<ReportingChainUser[]> {
    const response = await api.get<ReportingChainUser[]>(`${this.baseUrl}/${userId}/reporting-chain`);
    return response.data;
  }

  /**
   * Get reporting chain for the current member
   */
  async getMyReportingChain(): Promise<ReportingChainUser[]> {
    const response = await api.get<ReportingChainUser[]>(`${this.baseUrl}/me/reporting-chain`);
    return response.data;
  }

  /**
   * Get direct reports of the current member
   */
  async getMyDirectReports(): Promise<Member[]> {
    const response = await api.get<Member[]>(`${this.baseUrl}/me/direct-reports`);
    return response.data;
  }
}

// Export singleton instance
export const memberService = new MemberService();

/** @deprecated Use memberService */
export const userService = memberService;
